import { Controller } from "@hotwired/stimulus"

// Core::ModalComponent — a bespoke, accessible modal dialog.
//
// Per ADR MC-425 we import no Flowbite JS: the open/close, focus trap, ESC
// dismiss, body scroll lock, and focus restore are all implemented here,
// against the interactive-primitive contract in docs/13-ui-conventions.md.
//
// Lifecycle:
//   - connect() is idempotent and self-contained. It binds the single
//     document-level keydown listener (ESC + Tab trap) and reconciles the
//     surface to the open/closed state encoded in `data-state` on the
//     element — so a Turbo-cache snapshot that paints a stale state (the
//     instant back/forward repaint, before the network response lands) is
//     corrected on connect. All state is derived from the DOM, never from
//     module- or closure-level state that would outlive a navigation.
//   - disconnect() tears down everything connect() created: the keydown
//     listener and any body scroll lock. Without this, every Turbo
//     navigation through a page with a modal would leak a document handler.
//
// Open flow: a trigger (`data-action="core--modal#open"`) records the
// element to restore focus to, reveals the surface, and moves focus into
// the panel. Close flow: the × button, the backdrop, or ESC hides the
// surface and restores focus to the trigger.
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "[tabindex]:not([tabindex='-1'])"
].join(",")

export default class extends Controller {
  static targets = ["overlay", "panel"]

  connect() {
    this.onKeydown = this.onKeydown.bind(this)
    document.addEventListener("keydown", this.onKeydown)
    // Reconcile the scroll lock to the current (possibly cache-restored)
    // state. The overlay's visibility is already correct from the server
    // render / the snapshot, so we only sync the side effect here.
    if (this.isOpen()) {
      this.lockScroll()
    } else {
      this.unlockScroll()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKeydown)
    this.unlockScroll()
  }

  open(event) {
    event?.preventDefault()
    if (this.isOpen()) return
    // Remember what to restore focus to on close — usually the trigger.
    this.returnFocusTo = document.activeElement
    this.setOpen(true)
    this.focusInitial()
  }

  close(event) {
    event?.preventDefault()
    if (!this.isOpen()) return
    this.setOpen(false)
    this.restoreFocus()
  }

  onKeydown(event) {
    if (!this.isOpen()) return
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "Tab") {
      this.trapFocus(event)
    }
  }

  // --- state -------------------------------------------------------------

  isOpen() {
    return this.element.dataset.state === "open"
  }

  setOpen(open) {
    this.element.dataset.state = open ? "open" : "closed"
    if (this.hasOverlayTarget) this.overlayTarget.hidden = !open
    if (open) {
      this.lockScroll()
    } else {
      this.unlockScroll()
    }
  }

  // --- focus management --------------------------------------------------

  focusableElements() {
    if (!this.hasPanelTarget) return []
    return Array.from(this.panelTarget.querySelectorAll(FOCUSABLE)).filter(
      (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1
    )
  }

  focusInitial() {
    if (!this.hasPanelTarget) return
    const target = this.focusableElements()[0] || this.panelTarget
    // Defer a frame so the element is no longer `hidden` when we focus it.
    requestAnimationFrame(() => target.focus())
  }

  restoreFocus() {
    const el = this.returnFocusTo
    this.returnFocusTo = null
    if (el && typeof el.focus === "function" && document.contains(el)) {
      el.focus()
    }
  }

  // Keep Tab / Shift+Tab inside the panel: cycling past either end wraps to
  // the other, and any stray focus outside the panel is pulled back in.
  trapFocus(event) {
    if (!this.hasPanelTarget) return
    const focusables = this.focusableElements()
    if (focusables.length === 0) {
      event.preventDefault()
      this.panelTarget.focus()
      return
    }
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    const inside = this.panelTarget.contains(active)
    if (event.shiftKey) {
      if (active === first || !inside) {
        event.preventDefault()
        last.focus()
      }
    } else if (active === last || !inside) {
      event.preventDefault()
      first.focus()
    }
  }

  // --- body scroll lock --------------------------------------------------

  lockScroll() {
    if (this.scrollLocked) return
    this.previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    this.scrollLocked = true
  }

  unlockScroll() {
    if (!this.scrollLocked) return
    document.body.style.overflow = this.previousOverflow || ""
    this.scrollLocked = false
  }
}
